import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, AlertCircle } from "lucide-react";
import { PROPERTY_SUBTYPES, shouldShowField } from "@/data/propertyFormConstants";
import { INDIAN_STATES, CITIES_BY_STATE } from "@/data/indianLocations";
import { handleNumericKeyDown, handleNumericPaste } from "@/utils/propertyFormValidation";
import { numberToIndianWords, formatIndianShort } from "@/utils/indianNumberFormat";

// Dynamic placeholder maps based on property type + subtype
const TITLE_PLACEHOLDERS = {
  residential: {
    apartment: "Spacious 3BHK Apartment in Prime Location",
    independent_house: "Independent House with Garden in Gated Community",
    villa: "Luxury Villa with Pool in Premium Township",
    bungalow: "Modern Bungalow in Quiet Neighborhood",
    penthouse: "Premium Penthouse with Panoramic City Views",
    builder_floor: "Builder Floor with Private Terrace",
    row_house: "Row House in Well-Planned Township",
    twin_bungalow: "Twin Bungalow with Shared Amenities",
    farmhouse: "Farmhouse with Large Open Land",
    bungalow_scheme: "Bungalow Scheme Plot in Developing Area",
    _default: "Premium Residential Property in Great Location",
  },
  commercial: {
    office_space: "Prime Office Space in Business District",
    shop: "Corner Shop in High-Footfall Commercial Area",
    showroom: "Spacious Showroom on Main Road",
    warehouse: "Industrial Warehouse with Loading Dock",
    commercial_building: "Commercial Building with Multiple Units",
    mall: "Retail Space in Popular Mall",
    industrial_shed: "Industrial Shed with Power Supply",
    it_park: "Office Unit in IT Park with Amenities",
    hotel: "Hotel Property with Multiple Rooms",
    lodge: "Lodge with Rooms in Tourist Area",
    co_living: "Co-Living Space with Shared Amenities",
    co_working: "Co-Working Space with Modern Setup",
    business_center: "Business Center with Conference Facilities",
    hospital: "Hospital Property with Medical Setup",
    school: "School Property with Playground",
    college: "College Building with Campus",
    banquet_hall: "Banquet Hall for Events and Functions",
    _default: "Commercial Property in Strategic Location",
  },
  land: {
    residential_plot: "Residential Plot in Developing Locality",
    commercial_plot: "Commercial Plot on Main Road",
    agricultural_land: "Agricultural Land with Water Source",
    industrial_plot: "Industrial Plot near Highway",
    farm_land: "Farm Land with Farmhouse Potential",
    _default: "Plot/Land in Growing Area",
  },
};

const DESCRIPTION_PLACEHOLDERS = {
  residential: "Describe the property features, number of rooms, floor, society amenities, nearby landmarks, and connectivity...",
  commercial: "Describe the commercial space — carpet area, configuration, parking, power backup, lift access, and location advantages...",
  land: "Describe the plot — dimensions, road access, surrounding development, zoning, water/electricity availability...",
};

const getTitlePlaceholder = (propertyType, propertySubtype) => {
  const typeMap = TITLE_PLACEHOLDERS[propertyType] || TITLE_PLACEHOLDERS.residential;
  return typeMap[propertySubtype] || typeMap._default;
};

const getDescriptionPlaceholder = (propertyType) => {
  return DESCRIPTION_PLACEHOLDERS[propertyType] || DESCRIPTION_PLACEHOLDERS.residential;
};

export const BasicDetailsTab = ({
  formData,
  setFormData,
  handleChange,
  handleTextChange,
  isFieldInvalid,
  textWarnings,
  invalidFields,
  setInvalidFields,
  currentUserRole,
}) => {
  return (
<div className="space-y-6">
  {/* Property Type, Category, Purpose - FIRST */}
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <div>
      <Label htmlFor="property_type">Property Type *</Label>
      <select
        id="property_type"
        name="property_type"
        value={formData.property_type}
        onChange={(e) => {
          handleChange(e);
          setFormData(prev => ({ ...prev, property_subtype: "" }));
        }}
        className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
        required
        data-testid="property-type-select"
      >
        <option value="residential">Residential</option>
        <option value="commercial">Commercial</option>
        <option value="land">Land/Plot</option>
        <option value="mixed">Mixed</option>
      </select>
    </div>

    <div>
      <Label htmlFor="property_subtype" className={isFieldInvalid('property_subtype') ? 'text-red-600' : ''}>
        Property Category *
      </Label>
      <select
        id="property_subtype"
        name="property_subtype"
        value={formData.property_subtype}
        onChange={(e) => {
          handleChange(e);
          if (invalidFields.includes('property_subtype') && e.target.value) {
            setInvalidFields(prev => prev.filter(f => f !== 'property_subtype'));
          }
        }}
        className={`mt-1 h-11 w-full rounded-sm border px-3 text-sm ${isFieldInvalid('property_subtype') ? 'border-red-500 border-2 bg-red-50' : 'border-slate-200 bg-white'}`}
        required
        data-testid="property-category-select"
      >
        <option value="">Select Category</option>
        {PROPERTY_SUBTYPES[formData.property_type]?.map(subtype => (
          <option key={subtype.value} value={subtype.value}>
            {subtype.label}
          </option>
        ))}
      </select>
      {isFieldInvalid('property_subtype') && (
        <p className="text-red-500 text-sm mt-1">This field is required</p>
      )}
    </div>

    <div>
      <Label htmlFor="purpose">Purpose *</Label>
      <select
        id="purpose"
        name="purpose"
        data-testid="purpose-select"
        value={formData.purpose}
        onChange={handleChange}
        className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
        required
      >
        <option value="sale">Sale</option>
        <option value="resale">Resale</option>
        <option value="lease">Lease</option>
        <option value="rent">Rent</option>
        <option value="bts">BTS (Built-to-Suit)</option>
        <option value="purchase">Purchase</option>
      </select>
    </div>
  </div>

  {/* Field Visibility Preview - Shows when category is selected */}
  {formData.property_subtype && (
    <div className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-sm p-4" data-testid="field-preview">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-[#c9a227]" />
        <span className="text-xs font-semibold text-[#0f2744] uppercase tracking-wide">Form Preview for {PROPERTY_SUBTYPES[formData.property_type]?.find(s => s.value === formData.property_subtype)?.label}</span>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
        {formData.property_type === 'residential' && (
          <>
            <span>Bedrooms</span><span>Bathrooms</span><span>Furnishing</span><span>Parking</span>
            <span>Tower/Wing</span><span>Floor Info</span><span>Facing</span><span>Age</span>
            <span>Room Measurements</span><span>Amenities</span>
          </>
        )}
        {formData.property_type === 'commercial' && (
          <>
            <span>Furnishing</span><span>Parking</span><span>Tower/Wing</span><span>Floor Info</span>
            <span>Facing</span><span>Power Backup</span><span>Lift/Escalator</span>
            {shouldShowField('office_rooms', formData.property_type, formData.property_subtype) && <span>Office Config</span>}
            {shouldShowField('frontage_width', formData.property_type, formData.property_subtype) && <span>Shop Details</span>}
            {shouldShowField('loading_docks', formData.property_type, formData.property_subtype) && <span>Warehouse Details</span>}
            {shouldShowField('total_rooms', formData.property_type, formData.property_subtype) && <span>Hospitality Details</span>}
            <span>Space Measurements</span><span>Amenities</span>
          </>
        )}
        {formData.property_type === 'land' && (
          <>
            <span>Plot Dimensions</span><span>Facing</span><span>Zoning</span>
            <span>Road Width</span><span>Corner Plot</span><span>Boundary Wall</span>
            {shouldShowField('water_source', formData.property_type, formData.property_subtype) && <span>Water Source</span>}
            {shouldShowField('existing_crops', formData.property_type, formData.property_subtype) && <span>Agriculture Details</span>}
          </>
        )}
        {formData.property_type === 'mixed' && (
          <>
            <span>Total Floors</span><span>Parking</span><span>Furnishing</span>
            <span>Power Backup</span><span>Lift</span><span>Facing</span>
            <span>Tower/Wing</span><span>Floor Areas</span><span>Amenities</span>
          </>
        )}
      </div>
      {currentUserRole === 'sourcing_manager' && formData.property_type !== 'land' && (
        <p className="text-[10px] text-amber-700 mt-2 font-medium">Tower & Wing are required for Sourcing Managers</p>
      )}
    </div>
  )}

  {/* Property Title - AFTER Type/Category/Purpose */}
  <div>
    <Label htmlFor="title" className={isFieldInvalid('title') ? 'text-red-600' : ''}>
      Property Title *
    </Label>
    <Input
      id="title"
      name="title"
      value={formData.title}
      onChange={(e) => {
        handleTextChange(e);
        if (invalidFields.includes('title') && e.target.value) {
          setInvalidFields(prev => prev.filter(f => f !== 'title'));
        }
      }}
      className={`mt-1 h-11 rounded-sm ${textWarnings.title ? 'border-red-500 focus:border-red-500' : ''} ${isFieldInvalid('title') ? 'border-red-500 border-2 bg-red-50' : ''}`}
      required
      placeholder={getTitlePlaceholder(formData.property_type, formData.property_subtype)}
      data-testid="property-title-input"
    />
    {textWarnings.title && (
      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
        <AlertCircle className="h-4 w-4" />
        {textWarnings.title}
      </p>
    )}
    {isFieldInvalid('title') && !textWarnings.title && (
      <p className="text-red-500 text-sm mt-1">This field is required</p>
    )}
  </div>

  {/* Description */}
  <div>
    <Label htmlFor="description" className={isFieldInvalid('description') ? 'text-red-600' : ''}>
      Description *
    </Label>
    <Textarea
      id="description"
      name="description"
      value={formData.description}
      onChange={(e) => {
        handleTextChange(e);
        if (invalidFields.includes('description') && e.target.value) {
          setInvalidFields(prev => prev.filter(f => f !== 'description'));
        }
      }}
      className={`mt-1 rounded-sm min-h-[100px] ${textWarnings.description ? 'border-red-500 focus:border-red-500' : ''} ${isFieldInvalid('description') ? 'border-red-500 border-2 bg-red-50' : ''}`}
      required
      placeholder={getDescriptionPlaceholder(formData.property_type)}
      data-testid="property-description-input"
    />
    {textWarnings.description && (
      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
        <AlertCircle className="h-4 w-4" />
        {textWarnings.description}
      </p>
    )}
    {isFieldInvalid('description') && !textWarnings.description && (
      <p className="text-red-500 text-sm mt-1">This field is required</p>
    )}
  </div>

  {/* Property Size Details - Only for Full Building Types (non-mandatory) */}
  {['commercial_building', 'mall', 'warehouse', 'industrial_shed', 'it_park', 'hotel', 'lodge', 
    'co_living', 'co_working', 'business_center', 'hospital', 'school', 'college', 'banquet_hall',
    'independent_house', 'villa', 'bungalow', 'bungalow_scheme', 'farmhouse', 'penthouse', 'builder_floor',
    'twin_bungalow', 'row_house'].includes(formData.property_subtype) && (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
    <h4 className="font-semibold text-[#0f2744] mb-3">
      Property Size Details (Full Building)
    </h4>
    <p className="text-xs text-slate-500 mb-3">Enter the building size details for full building properties (optional - used for price/rent calculation)</p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <Label htmlFor="floor_plate_area">
          {['independent_house', 'villa', 'bungalow', 'bungalow_scheme', 'twin_bungalow', 'row_house', 'farmhouse', 'penthouse', 'builder_floor'].includes(formData.property_subtype)
            ? 'Area of Plot (sq ft)'
            : 'Area of Floor Plate (sq ft)'}
        </Label>
        <Input
          id="floor_plate_area"
          name="floor_plate_area"
          value={formData.floor_plate_area}
          onChange={handleChange}
          onKeyDown={handleNumericKeyDown}
          onPaste={handleNumericPaste}
          className="mt-1 h-11 rounded-lg"
          placeholder="e.g., 10000"
        />
        <p className="text-xs text-slate-500 mt-1">
          {['independent_house', 'villa', 'bungalow', 'bungalow_scheme', 'twin_bungalow', 'row_house', 'farmhouse', 'penthouse', 'builder_floor'].includes(formData.property_subtype)
            ? 'Total plot area'
            : 'Per floor plate size'}
        </p>
      </div>
      <div>
        <Label htmlFor="total_carpet_area">Total Carpet Area (sq ft)</Label>
        <Input
          id="total_carpet_area"
          name="total_carpet_area"
          value={formData.total_carpet_area}
          onChange={handleChange}
          onKeyDown={handleNumericKeyDown}
          onPaste={handleNumericPaste}
          className="mt-1 h-11 rounded-lg"
          placeholder="e.g., 50000"
        />
        <p className="text-xs text-slate-500 mt-1">Total usable floor area</p>
      </div>
      <div>
        <Label htmlFor="total_saleable_area">Total Saleable Area (sq ft)</Label>
        <Input
          id="total_saleable_area"
          name="total_saleable_area"
          value={formData.total_saleable_area}
          onChange={handleChange}
          onKeyDown={handleNumericKeyDown}
          onPaste={handleNumericPaste}
          className="mt-1 h-11 rounded-lg"
          placeholder="e.g., 60000"
        />
        <p className="text-xs text-slate-500 mt-1">Total saleable/leasable area</p>
      </div>
    </div>
  </div>
  )}

  {/* Price Breakdown Section - Different for Sale vs Rent/Lease */}
  {formData.purpose !== 'bts' ? (
    <>
      {/* Sale/Resale Pricing */}
      {(formData.purpose === 'sale' || formData.purpose === 'resale' || formData.purpose === 'purchase') ? (
        <>
          {/* Price Breakdown Header */}
          <div className="bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] text-white rounded-md px-4 py-2">
            <p className="text-sm font-medium">Enter either Agreement Value or All-Inclusive Price to calculate Rate per sq.ft</p>
          </div>

          {/* Step 1: Agreement Value (FIRST) */}
          <div className={`bg-slate-50 border-2 rounded-lg p-4 ${isFieldInvalid('all_inclusive_price') && !formData.agreement_value ? 'border-red-500' : 'border-[#c9a227]'}`}>
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#c9a227] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
              Agreement Value (For Registration)
            </h4>
            <div>
              <Label htmlFor="agreement_value" className={isFieldInvalid('all_inclusive_price') && !formData.agreement_value && !formData.all_inclusive_price ? 'text-red-600' : ''}>
                Agreement Value (₹) {!formData.all_inclusive_price && '*'}
              </Label>
              <Input
                id="agreement_value"
                name="agreement_value"
                type="number"
                value={formData.agreement_value}
                onChange={(e) => {
                  handleChange(e);
                  if (invalidFields.includes('all_inclusive_price') && e.target.value) {
                    setInvalidFields(prev => prev.filter(f => f !== 'all_inclusive_price'));
                  }
                }}
                onKeyDown={handleNumericKeyDown}
                onPaste={handleNumericPaste}
                className={`mt-1 h-11 rounded-lg text-lg font-semibold ${isFieldInvalid('all_inclusive_price') && !formData.agreement_value && !formData.all_inclusive_price ? 'border-red-500 border-2 bg-red-50' : ''}`}
                placeholder="90,00,000"
              />
              {isFieldInvalid('all_inclusive_price') && !formData.agreement_value && !formData.all_inclusive_price ? (
                <p className="text-red-500 text-sm mt-1">Either Agreement Value or All-Inclusive Price is required</p>
              ) : (
                <p className="text-xs text-slate-500 mt-1">Value registered in sale deed (Base for stamp duty & GST)</p>
              )}
              {formData.agreement_value && (
                <p className="text-xs text-[#0f2744] mt-1 font-medium">
                  {formatIndianShort(formData.agreement_value)} • {numberToIndianWords(formData.agreement_value)}
                </p>
              )}
            </div>
          </div>

          {/* Step 2: All-Inclusive Price */}
          <div className={`bg-[#fffdf5] border-2 rounded-lg p-4 ${isFieldInvalid('all_inclusive_price') && !formData.all_inclusive_price && !formData.agreement_value ? 'border-red-500' : 'border-[#c9a227]/50'}`}>
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#0f2744] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
              All-Inclusive Price (Total Asking Price)
            </h4>
            <div>
              <Label htmlFor="all_inclusive_price" className={isFieldInvalid('all_inclusive_price') && !formData.all_inclusive_price && !formData.agreement_value ? 'text-red-600' : ''}>
                All-Inclusive Price (₹) {!formData.agreement_value && '*'}
              </Label>
              <Input
                id="all_inclusive_price"
                name="all_inclusive_price"
                type="number"
                value={formData.all_inclusive_price}
                onChange={(e) => {
                  handleChange(e);
                  if (invalidFields.includes('all_inclusive_price') && e.target.value) {
                    setInvalidFields(prev => prev.filter(f => f !== 'all_inclusive_price'));
                  }
                }}
                onKeyDown={handleNumericKeyDown}
                onPaste={handleNumericPaste}
                className={`mt-1 h-11 rounded-lg text-lg font-semibold ${isFieldInvalid('all_inclusive_price') && !formData.all_inclusive_price && !formData.agreement_value ? 'border-red-500 border-2 bg-red-50' : ''}`}
                placeholder="1,00,00,000"
              />
              {isFieldInvalid('all_inclusive_price') && !formData.all_inclusive_price && !formData.agreement_value ? (
                <p className="text-red-500 text-sm mt-1">Either Agreement Value or All-Inclusive Price is required</p>
              ) : (
                <p className="text-xs text-slate-500 mt-1">Final price buyer will pay</p>
              )}
              {formData.all_inclusive_price && (
                <p className="text-xs text-[#0f2744] mt-1 font-medium">
                  {formatIndianShort(formData.all_inclusive_price)} • {numberToIndianWords(formData.all_inclusive_price)}
                </p>
              )}
            </div>
          </div>

          {/* Step 3: Area Fields - Carpet, Built-up, Super Built-up */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#0f2744] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
              Area Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="area" className={isFieldInvalid('area') ? 'text-red-600' : ''}>
                  {formData.property_type === 'land' ? 'Plot Area' : formData.property_type === 'commercial' ? 'Carpet Area / Chargeable Area' : 'Carpet Area'} (sq ft) {!(formData.total_carpet_area || formData.floor_plate_area) && '*'}
                </Label>
                <Input
                  id="area"
                  name="area"
                  type="number"
                  value={formData.area || formData.total_carpet_area || formData.floor_plate_area || ''}
                  onChange={(e) => {
                    handleChange(e);
                    if (invalidFields.includes('area') && e.target.value) {
                      setInvalidFields(prev => prev.filter(f => f !== 'area'));
                    }
                  }}
                  onKeyDown={handleNumericKeyDown}
                  onPaste={handleNumericPaste}
                  className={`mt-1 h-11 rounded-lg ${isFieldInvalid('area') ? 'border-red-500 border-2 bg-red-50' : ''}`}
                  placeholder="e.g., 1500"
                />
                {isFieldInvalid('area') && !(formData.total_carpet_area || formData.floor_plate_area) ? (
                  <p className="text-red-500 text-sm mt-1">This field is required</p>
                ) : (formData.total_carpet_area || formData.floor_plate_area) && !formData.area ? (
                  <p className="text-xs text-green-600 mt-1">
                    Auto-filled from Property Size Details ({formData.total_carpet_area ? 'Total Carpet Area' : 'Floor Plate Area'})
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">{formData.property_type === 'land' ? 'Total plot area' : 'Total carpet/usable area'}</p>
                )}
              </div>
              {formData.property_type !== 'land' && (
              <>
              <div>
                <Label htmlFor="built_up_area_price">Built-up Area (sq ft)</Label>
                <Input
                  id="built_up_area_price"
                  name="built_up_area"
                  type="number"
                  value={formData.built_up_area}
                  onChange={handleChange}
                  onKeyDown={handleNumericKeyDown}
                  onPaste={handleNumericPaste}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="e.g., 1800"
                />
                <p className="text-xs text-slate-500 mt-1">Including walls (optional)</p>
              </div>
              <div>
                <Label htmlFor="super_built_up_price">Super Built-up Area (sq ft)</Label>
                <Input
                  id="super_built_up_price"
                  name="super_built_up_area"
                  type="number"
                  value={formData.super_built_up_area}
                  onChange={handleChange}
                  onKeyDown={handleNumericKeyDown}
                  onPaste={handleNumericPaste}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="e.g., 2000"
                />
                <p className="text-xs text-slate-500 mt-1">Including common areas (optional)</p>
              </div>
              </>
              )}
            </div>
          </div>

          {/* Step 4: Government Taxes */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#0f2744] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">4</span>
              Government Taxes (On Agreement Value)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="stamp_duty_percent">Stamp Duty (%)</Label>
                <Input
                  id="stamp_duty_percent"
                  name="stamp_duty_percent"
                  type="number"
                  value={formData.stamp_duty_percent}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="7"
                />
                <p className="text-xs text-slate-500 mt-1">Default: 7% (Maharashtra)</p>
              </div>
              <div>
                <Label htmlFor="gst_percent">GST (%)</Label>
                <Input
                  id="gst_percent"
                  name="gst_percent"
                  type="number"
                  value={formData.gst_percent || (formData.property_type === 'commercial' ? '12' : '5')}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                  placeholder={formData.property_type === 'commercial' ? '12' : '5'}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.property_type === 'commercial' ? 'Commercial: 12%' : 'Residential/Land: 5%'}
                </p>
              </div>
            </div>
          </div>

          {/* Step 5: Infrastructure & Other Costs */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#0f2744] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">5</span>
              Infrastructure & Other Costs
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="maintenance_per_sqft">Maintenance (₹/sq.ft)</Label>
                <Input
                  id="maintenance_per_sqft"
                  name="maintenance_per_sqft"
                  type="number"
                  value={formData.maintenance_per_sqft}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="100"
                />
              </div>
              <div>
                <Label htmlFor="corpus_fund">Corpus Fund (₹)</Label>
                <Input
                  id="corpus_fund"
                  name="corpus_fund"
                  type="number"
                  value={formData.corpus_fund}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="50000"
                />
                {formData.corpus_fund && (
                  <p className="text-xs text-[#0f2744] mt-1 font-medium">
                    {formatIndianShort(formData.corpus_fund)} • {numberToIndianWords(formData.corpus_fund)}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="infrastructure_cost">Infrastructure Cost (₹)</Label>
                <Input
                  id="infrastructure_cost"
                  name="infrastructure_cost"
                  type="number"
                  value={formData.infrastructure_cost}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="100000"
                />
                {formData.infrastructure_cost && (
                  <p className="text-xs text-[#0f2744] mt-1 font-medium">
                    {formatIndianShort(formData.infrastructure_cost)} • {numberToIndianWords(formData.infrastructure_cost)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Rent/Lease Pricing */
        <>
          {/* Rent Breakdown Header - Smaller */}
          <div className="bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] text-white rounded-md px-4 py-2">
            <p className="text-sm font-medium">Enter monthly rent and deposit details for lease/rent property</p>
          </div>

          {/* Step 1: Monthly Rent - Choose Lumpsum OR Per Sqft */}
          <div className="bg-[#fffdf5] border-2 border-[#c9a227] rounded-lg p-4">
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#c9a227] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span>
              Monthly Rent *
            </h4>
            
            {/* Rent Type Selection */}
            <div className="flex gap-4 mb-4">
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-all ${formData.rent_type === 'lumpsum' ? 'border-[#c9a227] bg-[#c9a227]/10' : 'border-slate-200 bg-white'}`}>
                <input
                  type="radio"
                  name="rent_type"
                  value="lumpsum"
                  checked={formData.rent_type === 'lumpsum'}
                  onChange={handleChange}
                  className="accent-[#c9a227]"
                />
                <span className="text-sm font-medium">Lumpsum Amount</span>
              </label>
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-all ${formData.rent_type === 'per_sqft' ? 'border-[#c9a227] bg-[#c9a227]/10' : 'border-slate-200 bg-white'}`}>
                <input
                  type="radio"
                  name="rent_type"
                  value="per_sqft"
                  checked={formData.rent_type === 'per_sqft'}
                  onChange={handleChange}
                  className="accent-[#c9a227]"
                />
                <span className="text-sm font-medium">Per Sq.Ft Rate</span>
              </label>
            </div>

            {/* Lumpsum Rent Input */}
            {formData.rent_type === 'lumpsum' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="monthly_rent" className={isFieldInvalid('monthly_rent') ? 'text-red-600' : ''}>
                    Monthly Rent (₹) *
                  </Label>
                  <Input
                    id="monthly_rent"
                    name="monthly_rent"
                    type="number"
                    value={formData.monthly_rent}
                    onChange={(e) => {
                      handleChange(e);
                      if (invalidFields.includes('monthly_rent') && e.target.value) {
                        setInvalidFields(prev => prev.filter(f => f !== 'monthly_rent'));
                      }
                    }}
                    onKeyDown={handleNumericKeyDown}
                    onPaste={handleNumericPaste}
                    className={`mt-1 h-11 rounded-lg text-lg font-semibold ${isFieldInvalid('monthly_rent') ? 'border-red-500 border-2 bg-red-50' : ''}`}
                    placeholder="50000"
                    min="0"
                    data-testid="monthly-rent-input"
                  />
                  {isFieldInvalid('monthly_rent') ? (
                    <p className="text-red-500 text-sm mt-1">This field is required</p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">Total monthly rent amount (numbers only)</p>
                  )}
                  {formData.monthly_rent && (
                    <p className="text-xs text-[#0f2744] mt-1 font-medium">
                      {formatIndianShort(formData.monthly_rent)} • {numberToIndianWords(formData.monthly_rent)}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="area" className={isFieldInvalid('area') ? 'text-red-600' : ''}>
                    Carpet Area (sq.ft) {!(formData.total_carpet_area || formData.floor_plate_area) && '*'}
                  </Label>
                  <Input
                    id="area"
                    name="area"
                    type="number"
                    value={formData.area || formData.total_carpet_area || formData.floor_plate_area || ''}
                    onChange={(e) => {
                      handleChange(e);
                      if (invalidFields.includes('area') && e.target.value) {
                        setInvalidFields(prev => prev.filter(f => f !== 'area'));
                      }
                    }}
                    onKeyDown={handleNumericKeyDown}
                    onPaste={handleNumericPaste}
                    className={`mt-1 h-11 rounded-lg ${isFieldInvalid('area') ? 'border-red-500 border-2 bg-red-50' : ''}`}
                    placeholder="1000"
                    min="1"
                    data-testid="area-input"
                  />
                  {isFieldInvalid('area') && !(formData.total_carpet_area || formData.floor_plate_area) ? (
                    <p className="text-red-500 text-sm mt-1">This field is required</p>
                  ) : (formData.total_carpet_area || formData.floor_plate_area) && !formData.area ? (
                    <p className="text-xs text-green-600 mt-1">
                      Auto-filled from Property Size Details
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            {/* Per Sqft Rent Input */}
            {formData.rent_type === 'per_sqft' && (
              <div className="space-y-4">
                {/* Sqft Type Selection */}
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer border ${formData.rent_sqft_type === 'carpet' ? 'border-[#0f2744] bg-[#0f2744]/10' : 'border-slate-200'}`}>
                    <input
                      type="radio"
                      name="rent_sqft_type"
                      value="carpet"
                      checked={formData.rent_sqft_type === 'carpet'}
                      onChange={handleChange}
                      className="accent-[#0f2744]"
                    />
                    <span className="text-sm">Carpet Area</span>
                  </label>
                  <label className={`flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer border ${formData.rent_sqft_type === 'saleable' ? 'border-[#0f2744] bg-[#0f2744]/10' : 'border-slate-200'}`}>
                    <input
                      type="radio"
                      name="rent_sqft_type"
                      value="saleable"
                      checked={formData.rent_sqft_type === 'saleable'}
                      onChange={handleChange}
                      className="accent-[#0f2744]"
                    />
                    <span className="text-sm">Saleable/Built-up Area</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor={formData.rent_sqft_type === 'carpet' ? 'rent_per_sqft_carpet' : 'rent_per_sqft_builtup'}>
                      Rent per sq.ft (₹) *
                    </Label>
                    <Input
                      id={formData.rent_sqft_type === 'carpet' ? 'rent_per_sqft_carpet' : 'rent_per_sqft_builtup'}
                      name={formData.rent_sqft_type === 'carpet' ? 'rent_per_sqft_carpet' : 'rent_per_sqft_builtup'}
                      type="number"
                      value={formData.rent_sqft_type === 'carpet' ? formData.rent_per_sqft_carpet : formData.rent_per_sqft_builtup}
                      onChange={handleChange}
                      className="mt-1 h-11 rounded-lg text-lg font-semibold"
                      placeholder="50"
                    />
                  </div>
                  <div>
                    <Label htmlFor="area">
                      {formData.rent_sqft_type === 'carpet' ? 'Carpet' : 'Saleable'} Area (sq.ft) {!(formData.total_carpet_area || formData.floor_plate_area || formData.total_saleable_area) && '*'}
                    </Label>
                    <Input
                      id="area"
                      name="area"
                      type="number"
                      value={formData.area || (formData.rent_sqft_type === 'carpet' ? (formData.total_carpet_area || formData.floor_plate_area) : formData.total_saleable_area) || ''}
                      onChange={handleChange}
                      onKeyDown={handleNumericKeyDown}
                      onPaste={handleNumericPaste}
                      className="mt-1 h-11 rounded-lg"
                      placeholder="1000"
                    />
                    {((formData.rent_sqft_type === 'carpet' && (formData.total_carpet_area || formData.floor_plate_area)) ||
                      (formData.rent_sqft_type === 'saleable' && formData.total_saleable_area)) && !formData.area && (
                      <p className="text-xs text-green-600 mt-1">
                        Auto-filled from Property Size Details
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Calculated Monthly Rent</Label>
                    <div className="mt-1 h-11 rounded-lg bg-slate-100 flex items-center px-3 text-lg font-semibold text-[#0f2744]">
                      ₹{(() => {
                        const rate = parseFloat(formData.rent_sqft_type === 'carpet' ? formData.rent_per_sqft_carpet : formData.rent_per_sqft_builtup) || 0;
                        const areaValue = parseFloat(formData.area || (formData.rent_sqft_type === 'carpet' ? (formData.total_carpet_area || formData.floor_plate_area) : formData.total_saleable_area)) || 0;
                        return (rate * areaValue).toLocaleString('en-IN');
                      })()}
                    </div>
                    {(() => {
                      const rate = parseFloat(formData.rent_sqft_type === 'carpet' ? formData.rent_per_sqft_carpet : formData.rent_per_sqft_builtup) || 0;
                      const areaValue = parseFloat(formData.area || (formData.rent_sqft_type === 'carpet' ? (formData.total_carpet_area || formData.floor_plate_area) : formData.total_saleable_area)) || 0;
                      const calculatedRent = rate * areaValue;
                      return calculatedRent > 0 ? (
                        <p className="text-xs text-[#0f2744] mt-1 font-medium">
                          {formatIndianShort(calculatedRent)} • {numberToIndianWords(calculatedRent)}
                        </p>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Rent Negotiable */}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#c9a227]/30">
              <Checkbox
                id="rent_negotiable"
                checked={formData.rent_negotiable}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rent_negotiable: checked }))}
              />
              <Label htmlFor="rent_negotiable" className="text-sm cursor-pointer">Rent is negotiable</Label>
            </div>
          </div>

          {/* Step 2: Security Deposit - Choose Lumpsum OR Months */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#0f2744] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">2</span>
              Security Deposit
            </h4>
            
            {/* Deposit Type Selection */}
            <div className="flex gap-4 mb-4">
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-all ${formData.security_deposit_type === 'lumpsum' ? 'border-[#0f2744] bg-[#0f2744]/10' : 'border-slate-200 bg-white'}`}>
                <input
                  type="radio"
                  name="security_deposit_type"
                  value="lumpsum"
                  checked={formData.security_deposit_type === 'lumpsum'}
                  onChange={handleChange}
                  className="accent-[#0f2744]"
                />
                <span className="text-sm font-medium">Lumpsum Amount</span>
              </label>
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border-2 transition-all ${formData.security_deposit_type === 'months' ? 'border-[#0f2744] bg-[#0f2744]/10' : 'border-slate-200 bg-white'}`}>
                <input
                  type="radio"
                  name="security_deposit_type"
                  value="months"
                  checked={formData.security_deposit_type === 'months'}
                  onChange={handleChange}
                  className="accent-[#0f2744]"
                />
                <span className="text-sm font-medium">X Month(s) Rent</span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.security_deposit_type === 'lumpsum' ? (
                <div>
                  <Label htmlFor="security_deposit">Security Deposit (₹) *</Label>
                  <Input
                    id="security_deposit"
                    name="security_deposit"
                    type="number"
                    value={formData.security_deposit}
                    onChange={handleChange}
                    onKeyDown={handleNumericKeyDown}
                    onPaste={handleNumericPaste}
                    className="mt-1 h-11 rounded-lg"
                    placeholder="100000"
                    min="0"
                    data-testid="security-deposit-input"
                  />
                  {formData.security_deposit && (
                    <p className="text-xs text-[#0f2744] mt-1 font-medium">
                      {formatIndianShort(formData.security_deposit)} • {numberToIndianWords(formData.security_deposit)}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="security_deposit_months">Deposit in Months *</Label>
                    <select
                      id="security_deposit_months"
                      name="security_deposit_months"
                      value={formData.security_deposit_months}
                      onChange={handleChange}
                      className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744]"
                    >
                      <option value="">Select months</option>
                      <option value="1">1 Month</option>
                      <option value="2">2 Months</option>
                      <option value="3">3 Months</option>
                      <option value="6">6 Months</option>
                      <option value="10">10 Months</option>
                      <option value="12">12 Months</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Standard: 2-3 months for residential, 6-10 months for commercial</p>
                  </div>
                  {formData.security_deposit_months && formData.monthly_rent && (
                    <div>
                      <Label>Calculated Deposit</Label>
                      <div className="mt-1 h-11 rounded-lg bg-slate-100 flex items-center px-3 text-lg font-semibold text-[#0f2744]">
                        ₹{(parseFloat(formData.monthly_rent) * parseFloat(formData.security_deposit_months)).toLocaleString('en-IN')}
                      </div>
                      {(() => {
                        const calculatedDeposit = parseFloat(formData.monthly_rent) * parseFloat(formData.security_deposit_months);
                        return calculatedDeposit > 0 ? (
                          <p className="text-xs text-[#0f2744] mt-1 font-medium">
                            {formatIndianShort(calculatedDeposit)} • {numberToIndianWords(calculatedDeposit)}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Step 3: Lease Terms */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#0f2744] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">3</span>
              Lease Terms
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="lock_in_period">Lock-in Period</Label>
                <select
                  id="lock_in_period"
                  name="lock_in_period"
                  value={formData.lock_in_period}
                  onChange={handleChange}
                  className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744]"
                >
                  <option value="">Select period</option>
                  <option value="none">No Lock-in</option>
                  <option value="6">6 Months</option>
                  <option value="11">11 Months</option>
                  <option value="12">1 Year</option>
                  <option value="24">2 Years</option>
                  <option value="36">3 Years</option>
                </select>
              </div>
              <div>
                <Label htmlFor="lease_duration">Lease Duration</Label>
                <select
                  id="lease_duration"
                  name="lease_duration"
                  value={formData.lease_duration}
                  onChange={handleChange}
                  className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744]"
                >
                  <option value="">Select duration</option>
                  <option value="11">11 Months</option>
                  <option value="12">1 Year</option>
                  <option value="24">2 Years</option>
                  <option value="36">3 Years</option>
                  <option value="60">5 Years</option>
                  <option value="120">10 Years</option>
                </select>
              </div>
              <div>
                <Label htmlFor="rent_escalation">Yearly Rent Escalation (%)</Label>
                <Input
                  id="rent_escalation"
                  name="rent_escalation"
                  type="number"
                  value={formData.rent_escalation}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                  placeholder="5"
                />
                <p className="text-xs text-slate-500 mt-1">Typical: 5-10% yearly</p>
              </div>
            </div>
          </div>

          {/* Step 4: Inclusions & Preferences */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
              <span className="bg-[#0f2744] text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">4</span>
              Inclusions & Preferences
            </h4>
            
            {/* Inclusions */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="maintenance_included"
                  checked={formData.maintenance_included}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, maintenance_included: checked }))}
                />
                <Label htmlFor="maintenance_included" className="text-sm cursor-pointer">Maintenance Included</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="electricity_included"
                  checked={formData.electricity_included}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, electricity_included: checked }))}
                />
                <Label htmlFor="electricity_included" className="text-sm cursor-pointer">Electricity Included</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="water_included"
                  checked={formData.water_included}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, water_included: checked }))}
                />
                <Label htmlFor="water_included" className="text-sm cursor-pointer">Water Included</Label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="preferred_tenant">Preferred Tenant</Label>
                <select
                  id="preferred_tenant"
                  name="preferred_tenant"
                  value={formData.preferred_tenant}
                  onChange={handleChange}
                  className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744]"
                >
                  <option value="">No Preference</option>
                  <option value="family">Family</option>
                  <option value="bachelor_male">Bachelor (Male)</option>
                  <option value="bachelor_female">Bachelor (Female)</option>
                  <option value="company">Company/Corporate</option>
                  <option value="any">Anyone</option>
                </select>
              </div>
              <div>
                <Label htmlFor="available_from">Available From</Label>
                <Input
                  id="available_from"
                  name="available_from"
                  type="date"
                  value={formData.available_from}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-lg"
                />
              </div>
            </div>

            {/* Monthly Maintenance if not included */}
            {!formData.maintenance_included && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="maintenance_charges">Monthly Maintenance (₹)</Label>
                    <Input
                      id="maintenance_charges"
                      name="maintenance_charges"
                      type="number"
                      value={formData.maintenance_charges}
                      onChange={handleChange}
                      className="mt-1 h-11 rounded-lg"
                      placeholder="2000"
                    />
                    <p className="text-xs text-slate-500 mt-1">Separate from rent</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Rent Summary */}
          {formData.monthly_rent && (
            <div className="bg-white border-2 border-[#0f2744] rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-[#0f2744] mb-3">Rental Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-[#fffdf5] rounded-lg">
                  <p className="text-xs text-slate-500">Monthly Rent</p>
                  <p className="text-xl font-bold text-[#0f2744]">₹{Number(formData.monthly_rent).toLocaleString('en-IN')}</p>
                </div>
                {formData.security_deposit && (
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Security Deposit</p>
                    <p className="text-lg font-semibold text-[#0f2744]">₹{Number(formData.security_deposit).toLocaleString('en-IN')}</p>
                  </div>
                )}
                {formData.security_deposit_months && !formData.security_deposit && (
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Security Deposit</p>
                    <p className="text-lg font-semibold text-[#0f2744]">{formData.security_deposit_months} Month(s)</p>
                  </div>
                )}
                {formData.area && (
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Rent/sq.ft</p>
                    <p className="text-lg font-semibold text-[#0f2744]">₹{Math.round(formData.monthly_rent / formData.area)}</p>
                  </div>
                )}
                {formData.lock_in_period && (
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Lock-in Period</p>
                    <p className="text-lg font-semibold text-[#0f2744]">{formData.lock_in_period === 'none' ? 'None' : `${formData.lock_in_period} Months`}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Price Calculation Display - Reverse Calculation - Only for Sale/Resale */}
      {(formData.purpose === 'sale' || formData.purpose === 'resale' || formData.purpose === 'purchase') && (formData.all_inclusive_price || formData.agreement_value) && formData.area && (
        <div className="bg-white border-2 border-[#0f2744] rounded-lg p-4 mt-4">
          <h4 className="font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
            <span className="bg-[#c9a227] text-[#0f2744] w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">✓</span>
            Price Breakdown Summary
          </h4>
          <div className="space-y-2 text-sm">
            {/* Primary Price - whichever was provided */}
            {formData.all_inclusive_price && (
              <div className="flex justify-between text-lg font-bold text-[#c9a227] pb-2 border-b border-slate-200">
                <span>All-Inclusive Price</span>
                <span>₹{parseFloat(formData.all_inclusive_price || 0).toLocaleString('en-IN')}</span>
              </div>
            )}
            
            {/* Agreement Value */}
            {formData.agreement_value && (
              <div className={`flex justify-between pt-2 ${!formData.all_inclusive_price ? 'text-lg font-bold text-[#c9a227] pb-2 border-b border-slate-200' : ''}`}>
                <span className={formData.all_inclusive_price ? 'text-slate-600' : ''}>Agreement Value {formData.all_inclusive_price ? '(Registered)' : ''}</span>
                <span className={formData.all_inclusive_price ? 'font-medium' : ''}>₹{parseFloat(formData.agreement_value || 0).toLocaleString('en-IN')}</span>
              </div>
            )}
            
            {/* Taxes - only show if agreement value exists */}
            {formData.agreement_value && (
              <div className="pl-4 space-y-1 text-slate-500">
                <div className="flex justify-between">
                  <span>+ Stamp Duty ({formData.stamp_duty_percent || 7}%)</span>
                  <span>
                    ₹{(() => {
                      const agreementValue = parseFloat(formData.agreement_value || 0);
                      const stampDuty = parseFloat(formData.stamp_duty_percent || 7);
                      return (agreementValue * stampDuty / 100).toLocaleString('en-IN');
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>+ GST ({formData.gst_percent || (formData.property_type === 'commercial' ? 12 : 5)}%)</span>
                  <span>
                    ₹{(() => {
                      const agreementValue = parseFloat(formData.agreement_value || 0);
                      const gst = parseFloat(formData.gst_percent || (formData.property_type === 'commercial' ? 12 : 5));
                      return (agreementValue * gst / 100).toLocaleString('en-IN');
                    })()}
                  </span>
                </div>
              </div>
            )}
            
            {/* Calculated Rate */}
            <div className="border-t-2 border-[#0f2744] pt-3 mt-3">
              <div className="flex justify-between text-lg">
                <span className="font-bold text-[#0f2744]">Calculated Rate per sq.ft</span>
                <span className="font-bold text-[#0f2744]">
                  ₹{(() => {
                    const basePrice = parseFloat(formData.all_inclusive_price || formData.agreement_value || 0);
                    const area = parseFloat(formData.area || 1);
                    return Math.round(basePrice / area).toLocaleString('en-IN');
                  })()} /sq.ft
                </span>
              </div>
              {formData.all_inclusive_price && formData.agreement_value && (
                <div className="flex justify-between text-sm mt-2 text-slate-500">
                  <span>Base Rate (Agreement Value / Area)</span>
                  <span>
                    ₹{(() => {
                      const agreementValue = parseFloat(formData.agreement_value || 0);
                      const area = parseFloat(formData.area || 1);
                      return Math.round(agreementValue / area).toLocaleString('en-IN');
                    })()} /sq.ft
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hidden field to store calculated price */}
      <input type="hidden" name="price" value={formData.all_inclusive_price || formData.agreement_value || 0} />
      <input type="hidden" name="rate_per_sqft" value={(() => {
        const basePrice = parseFloat(formData.all_inclusive_price || formData.agreement_value || 0);
        const area = parseFloat(formData.area || 1);
        return Math.round(basePrice / area);
      })()} />
    </>
  ) : (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <p className="text-amber-800 text-sm">
        <strong>BTS (Built-to-Suit):</strong> Price breakdown is not applicable for Built-to-Suit properties. Please add rental terms in the description.
      </p>
    </div>
  )}

  {/* State and City Selection */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div>
      <Label htmlFor="state">State *</Label>
      <select
        id="state"
        name="state"
        value={formData.state}
        onChange={(e) => {
          handleChange(e);
          // Reset city when state changes
          setFormData(prev => ({ ...prev, city: "" }));
        }}
        className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f]"
        required
      >
        <option value="">Select State</option>
        {INDIAN_STATES.map(state => (
          <option key={state.value} value={state.value}>{state.label}</option>
        ))}
      </select>
    </div>
    <div>
      <Label htmlFor="city">City *</Label>
      <select
        id="city"
        name="city"
        value={formData.city}
        onChange={handleChange}
        className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f]"
        required
        disabled={!formData.state}
      >
        <option value="">{formData.state ? "Select City" : "Select State First"}</option>
        {formData.state && CITIES_BY_STATE[formData.state]?.map(city => (
          <option key={city.value} value={city.value}>{city.label}</option>
        ))}
      </select>
    </div>
  </div>

  <div>
    <Label htmlFor="location" className={isFieldInvalid('location') ? 'text-red-600' : ''}>
      Area / Locality *
    </Label>
    <Input
      id="location"
      name="location"
      value={formData.location}
      onChange={(e) => {
        handleChange(e);
        if (invalidFields.includes('location') && e.target.value) {
          setInvalidFields(prev => prev.filter(f => f !== 'location'));
        }
      }}
      className={`mt-1 h-11 rounded-sm ${isFieldInvalid('location') ? 'border-red-500 border-2 bg-red-50' : ''}`}
      required
      placeholder="e.g., Baner, Wakad, Koramangala"
    />
    {isFieldInvalid('location') ? (
      <p className="text-red-500 text-sm mt-1">This field is required</p>
    ) : (
      <p className="text-xs text-slate-500 mt-1">Enter the specific area or locality within the city</p>
    )}
  </div>

  <div>
    <Label htmlFor="status">Status *</Label>
    <select
      id="status"
      name="status"
      value={formData.status}
      onChange={handleChange}
      className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
      required
    >
      <option value="available">Available</option>
      <option value="sold">Sold</option>
      <option value="leased">Leased</option>
      <option value="rented">Rented</option>
    </select>
  </div>
</div>
  );
};
